/**
 * Rendering helpers for the two kinds of time a session carries, and nothing
 * else. No quantity and no money is formatted anywhere in this app — those
 * arrive as strings and are rendered as strings (§ 2.1).
 */

/**
 * A BC audit timestamp (`createdAt`, `submittedAt`, `postedAt`) for a manager to
 * read, in Tampa time.
 *
 * The zone is pinned rather than taken from the device for the same reason the
 * posting date is: the bakery is in Tampa, and "12:00" meaning something else
 * because an iPad came back from a trip is a confusing way to read a count's
 * history. An unparseable value is returned verbatim — showing BC's raw string
 * beats showing "Invalid Date".
 */
export function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "—";
  // BC's AL has no null datetime: an unset one arrives as `0001-01-01T00:00:00Z`,
  // which renders as "Dec 31, 1" in Tampa. It means "never happened", so it reads
  // as absent, same as null.
  if (isUnsetBcDate(value)) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** BC's "no value" for a date or datetime: year 1 (`0001-01-01...`), not null. */
export function isUnsetBcDate(value: string | null | undefined): boolean {
  return /^0001-01-01/.test((value ?? "").trim());
}

/**
 * A posting date (`YYYY-MM-DD`) as itself.
 *
 * Deliberately **not** parsed into a `Date` first: `new Date("2026-08-20")` is
 * midnight UTC, which renders as the 19th anywhere west of Greenwich. A posting
 * date is a calendar date BC holds, not an instant, so it is shown as the string
 * BC sent.
 */
export function formatPostingDate(value: string | null | undefined): string {
  return value && value.trim() && !isUnsetBcDate(value) ? value : "—";
}
